import React from 'react';
import { ArrowRight, Code, HandCoins, HardHat, Heartbeat, MapPin, MagnifyingGlass, SlidersHorizontal } from '@phosphor-icons/react';
import {
  LOCATION_CHIP_TEXT,
  locationChipValueClass,
  locationSegmentClass,
  NAV_PART,
  SEARCH_FIELD_PEEK_TILE,
  SEARCH_FILTERS_CHIP_COUNT,
  SEARCH_GO_COUNT,
  SEARCH_GO_SUFFIX,
  SEARCH_METER_FILL,
  SEARCH_METER_TRACK,
  SEARCH_SLOT_LABEL,
  searchFieldPeekClass,
  searchFiltersChipClass,
  searchGoButtonClass,
  type SearchBarSizeVariant,
} from './nav-styles';

/**
 * Presentational pieces of the Sage search command bar (2026-09-28). No hooks and no context,
 * only props, so the prerendered static twin (ResourceNavbar.tsx) renders the exact same markup
 * the hydrated hosts (Autocomplete.tsx, SearchLauncher.tsx) do. Each piece renders as a <button>
 * when given `onClick`, or as an <a> when given `href` (the static twin never hydrates, so it
 * can only use links).
 */

type LocationChipState = 'idle' | 'set' | 'excluded';

interface ActionProps {
  onClick?: () => void;
  href?: string;
}

function ActionElement({
  onClick,
  href,
  className,
  children,
  ...rest
}: ActionProps & { className: string; children: React.ReactNode } & Record<string, unknown>) {
  if (href !== undefined) {
    return (
      <a href={href} data-vike="false" className={className} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} {...rest}>
      {children}
    </button>
  );
}

export const SearchWhereChip: React.FC<
  ActionProps & {
    label: string;
    value: string;
    ariaLabel: string;
    state?: LocationChipState;
    sizeVariant?: SearchBarSizeVariant;
    ariaExpanded?: boolean;
  }
> = ({ onClick, href, label, value, ariaLabel, state = 'idle', sizeVariant = 'compact40', ariaExpanded }) => (
  <ActionElement
    onClick={onClick}
    href={href}
    data-jc-nav-part={NAV_PART.locationSegment}
    className={locationSegmentClass(sizeVariant, state)}
    aria-label={`${ariaLabel}: ${value}`}
    aria-expanded={ariaExpanded}
  >
    <span className={SEARCH_SLOT_LABEL}>{label}</span>
    <span className={locationChipValueClass(state)}>
      <MapPin weight="fill" className="w-[15px] h-[15px] xl:w-[13px] xl:h-[13px] shrink-0 text-jc-accent" aria-hidden />
      <span className={LOCATION_CHIP_TEXT}>{value}</span>
    </span>
  </ActionElement>
);

export const SearchFiltersChip: React.FC<ActionProps & { ariaLabel: string; count?: number }> = ({ onClick, href, ariaLabel, count = 0 }) => (
  <ActionElement
    onClick={onClick}
    href={href}
    data-jc-nav-part={NAV_PART.searchFiltersChip}
    className={searchFiltersChipClass(count > 0)}
    aria-label={count > 0 ? `${ariaLabel} (${count})` : ariaLabel}
  >
    <SlidersHorizontal weight="bold" className="w-5 h-5 shrink-0" aria-hidden />
    {count > 0 ? <span className={SEARCH_FILTERS_CHIP_COUNT}>{count > 99 ? '99+' : count}</span> : null}
  </ActionElement>
);

/**
 * The forest button. `countText` is the already-formatted live count, or null when it isn't
 * known yet: the button then collapses to a plain round search button rather than print a guess.
 * `type="submit"` is only for the static twin's no-JS <form>.
 */
export const SearchGoButton: React.FC<{
  countText: string | null;
  suffix: string;
  ariaLabel: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  countSlotAttr?: boolean;
}> = ({ countText, suffix, ariaLabel, onClick, type = 'button', countSlotAttr = false }) => (
  <button
    type={type}
    onClick={onClick}
    data-jc-nav-part={NAV_PART.searchGoButton}
    className={searchGoButtonClass(countText !== null)}
    aria-label={ariaLabel}
    {...(countSlotAttr ? { 'data-jc-search-go': '' } : {})}
  >
    {countText !== null ? (
      <>
        <span className={SEARCH_GO_COUNT} {...(countSlotAttr ? { 'data-jc-search-count': '' } : {})}>
          {countText}
        </span>
        <span className={SEARCH_GO_SUFFIX}>{suffix}</span>
        <ArrowRight weight="bold" className="w-[15px] h-[15px] shrink-0" aria-hidden />
      </>
    ) : (
      <MagnifyingGlass weight="bold" className="w-5 h-5 shrink-0" aria-hidden />
    )}
  </button>
);

/** The same four field glyphs the search panel's "Browse a field" grid leads with
 * (utils/search-panel/facet-icon.tsx FIELD_ICONS). Decorative: the whole slot is the control. */
const PEEK_ICONS = [Code, Heartbeat, HardHat, HandCoins];

export const SearchFieldPeek: React.FC<{ visible: boolean }> = ({ visible }) => (
  <span className={searchFieldPeekClass(visible)} aria-hidden data-jc-search-peek="">
    {PEEK_ICONS.map((Icon, i) => (
      <span key={i} className={SEARCH_FIELD_PEEK_TILE}>
        <Icon weight="bold" className="w-3.5 h-3.5" />
      </span>
    ))}
  </span>
);

/** Rounded to whole percent; a ratio this close to 1 means nothing is narrowed yet. */
export function meterPercent(ratio: number): number {
  return Math.max(2, Math.min(100, Math.round(ratio * 100)));
}

/** `ratio` in [0, 1] (current count / all open jobs); null removes the line. Stays mounted but
 * transparent while nothing is narrowed (100%), so the static twin's script can fade it in. */
export const SearchNarrowMeter: React.FC<{ ratio: number | null }> = ({ ratio }) =>
  ratio === null ? null : (
    <span className={SEARCH_METER_TRACK} aria-hidden data-jc-search-meter="" style={{ opacity: meterPercent(ratio) >= 100 ? 0 : 1 }}>
      <span className={SEARCH_METER_FILL} style={{ width: `${meterPercent(ratio)}%` }} />
    </span>
  );
